import { ChangeDetectionStrategy, Component, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { TabNavItemConfig } from '@vsolv/vectors-ui/app-nav';
import { SecurityService } from '@wsphere/staff/web';
import { Policy } from '@wsphere/warranties/domain';
import { combineLatest, map, startWith, switchMap } from 'rxjs';
import {
  ArchivePolicyDialog,
  CopyPolicyToEnvironmentDialog,
  DeletePolicyDialog,
  PublishPolicyDialog,
  RestorePolicyDialog,
} from '../../dialogs';
import { EnvironmentService, FeatureFlagsService, PolicyService } from '../../services';

@Component({ changeDetection: ChangeDetectionStrategy.Eager, templateUrl: './policy.page.html', standalone: false })
export class PolicyPage {
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private toastSvc: ToastService,
    private policySvc: PolicyService,
    private securitySvc: SecurityService,
    private envSvc: EnvironmentService,
    private featureFlagsSvc: FeatureFlagsService
  ) {}

  @ViewChild('publishDialog') publishDialog!: PublishPolicyDialog;
  @ViewChild('archiveDialog') archiveDialog!: ArchivePolicyDialog;
  @ViewChild('restoreDialog') restoreDialog!: RestorePolicyDialog;
  @ViewChild('deleteDialog') deleteDialog!: DeletePolicyDialog;
  @ViewChild('copyToEnvironmentDialog') copyToEnvironmentDialog!: CopyPolicyToEnvironmentDialog;

  readonly policy$ = this.policySvc.getPolicyObservable();

  draft = Policy.Status.DRAFT;
  published = Policy.Status.PUBLISHED;
  archived = Policy.Status.ARCHIVED;
  runoff = Policy.Status.RUNOFF;

  canCreate$ = this.securitySvc.globalDistributors$.pipe(
    switchMap(globalDistributors => {
      const permissionKeys = globalDistributors?.map(dist => dist.permissionKey) ?? null;
      return this.securitySvc.hasAccess('pol_Create', permissionKeys);
    })
  );

  canManageStatus$ = this.securitySvc.globalDistributors$.pipe(
    switchMap(globalDistributors => {
      const permissionKeys = globalDistributors?.map(dist => dist.permissionKey) ?? null;
      return this.securitySvc.hasAccess('pol_ManageStatus', permissionKeys);
    })
  );

  canViewClaimLifecycle$ = this.securitySvc.globalDistributors$.pipe(
    switchMap(globalDistributors => {
      const permissionKeys = globalDistributors?.map(dist => dist.permissionKey) ?? null;
      return this.securitySvc.hasAccess('pol_ViewLifecycle', permissionKeys).catch(() => false);
    }),
    startWith(false)
  );

  isSandbox = this.envSvc.isSandbox;

  private dynamicRatingEnabled$ = this.featureFlagsSvc.isEnabledState('DYNAMIC_RATING').pipe(
    map(enabled => enabled === true),
    startWith(false)
  );

  // Standard tabs remain available while optional permission and feature checks are unresolved.
  tabs$ = combineLatest([this.canViewClaimLifecycle$, this.dynamicRatingEnabled$]).pipe(
    map(([canViewLifecycle, dynamicRatingEnabled]) => {
      const tabs: TabNavItemConfig[] = [];

      tabs.push({ label: 'Details', routerLink: './' });
      tabs.push({ label: 'Plans', routerLink: './plans', exact: false });
      tabs.push({ label: 'Cancellation', routerLink: './cancellation', exact: false });
      tabs.push({ label: 'Documents', routerLink: './documents' });
      if (canViewLifecycle) tabs.push({ label: 'Claim Lifecycle', routerLink: './claim-lifecycle' });
      tabs.push({ label: 'Communications', routerLink: './communications' });
      if (dynamicRatingEnabled) tabs.push({ label: 'Dimensions', routerLink: './dimensions' });
      if (dynamicRatingEnabled) tabs.push({ label: 'Parties', routerLink: './parties' });

      return tabs;
    })
  );

  versions$ = this.policy$.pipe(
    switchMap(async policy => {
      const policyVersion = policy
        ? await this.policySvc.getVersions(policy.id).catch(({ error }) => {
            this.toastSvc.show({ type: 'error', title: 'Unable to load policy version', text: error.message });
            return null;
          })
        : null;
      return policyVersion;
    })
  );

  async duplicatePolicy(policyId: string) {
    const response = await this.policySvc.duplicate(policyId, false).catch(({ error }) => {
      this.toastSvc.show({
        type: 'error',
        title: 'Something went wrong',
        text: error.message,
      });
    });

    const duplicatedPolicyObj =
      response && typeof response === 'object' ? (response as { id: string; title?: string }) : undefined;
    const duplicatedPolicyId = duplicatedPolicyObj?.id;
    if (duplicatedPolicyId && duplicatedPolicyObj) {
      this.toastSvc.show({
        type: 'success',
        title: 'Policy has been duplicated',
        text: `${duplicatedPolicyObj.title} has been successfully duplicated`,
      });

      this.navigateTo(duplicatedPolicyId);
    }
  }

  async switchToVersion(versionId: string) {
    const activeTab = this.route.snapshot.firstChild?.routeConfig?.path;
    const currentPlan = this.route.snapshot.firstChild?.firstChild?.data?.['plan'] as Policy.Plan.Model | null;
    const currentPlanId = this.route.snapshot.firstChild?.firstChild?.params?.['planId'] ?? null;

    let targetPlanId: string | null = null;

    if (activeTab === 'plans' && currentPlanId) {
      const policy = await this.policySvc.getOne(versionId).catch(() => null);
      targetPlanId = this.findMatchingPlan(policy?.plans, currentPlan, currentPlanId);
    }

    this.policySvc.clearPlan();
    this.policySvc.clearPolicy();

    const activeSegments = [activeTab].filter(Boolean) as string[];
    if (targetPlanId) activeSegments.push(targetPlanId);

    this.navigateTo(versionId, activeSegments);
  }

  navigateTo(policyId?: string, childSegments: string[] = []) {
    const commands = policyId ? ['..', policyId, ...childSegments] : ['..'];
    this.router.navigate(commands, { relativeTo: this.route });
  }

  private findMatchingPlan(
    plans: Policy.Plan.Model[] | undefined | null,
    currentPlan: Policy.Plan.Model | null,
    currentPlanId: string | null
  ): string | null {
    if (!plans?.length) return null;

    const sortedPlans = [...plans].sort((a, b) => a.order - b.order || a.title.localeCompare(b.title));
    const planIdBase = currentPlanId?.split('-')[0] || '';

    const candidate =
      sortedPlans.find(p => p.id === currentPlanId) ||
      (currentPlan ? sortedPlans.find(p => p.order === currentPlan.order) : null) ||
      (currentPlan ? sortedPlans.find(p => p.title === currentPlan.title) : null) ||
      (planIdBase ? sortedPlans.find(p => p.id.startsWith(planIdBase)) : null) ||
      sortedPlans[0];

    return candidate?.id ?? null;
  }
}
